import makeErrorFromUnknown from '@/functions/makeErrorFromUnknown';
import config from '@/modules/config/functions/config';
import {
  AdminSettingsSchema,
  type AdminSettings,
} from '@/modules/core/types/AdminSettings';
import type { GetAdminSettingsResponse } from '@/modules/core/types/responses/GetAdminSettingsResponse';
import {
  BaseError,
  CoreAdminSettingsDataProcessingError,
  CoreAdminSettingsFetchError,
  CoreAdminSettingsGeneralError,
  CoreAdminSettingsInternalServerError,
  CoreAdminSettingsRequestError,
  CoreAdminSettingsServerConnectionError,
} from '@/modules/errors/constants/error-class-map.gen';
import logger from '@/modules/logger/constants/logger';
import attachCommonWretchErrorHandlers from '@/modules/query-client/functions/attachCommonWretchErrorHandlers';
import createBaseWretch from '@/modules/query-client/functions/createBaseWretch';
import createCommonWretchErrorHandlerMap from '@/modules/query-client/functions/createCommonWretchErrorHandlerMap';
import createQueryOptions from '@/modules/query-client/functions/createQueryOptions';
import handleWretchFetchError from '@/modules/query-client/functions/handleWretchFetchError';
import type { UserProvidedUseQueryOptions } from '@/modules/query-client/types/UserProvidedUseQueryOptions';
import { type } from 'arktype';
import { omit } from 'lodash-es';

/**
 * Create query options for useAdminSettingsQuery hook
 * @param param0 - Query options
 * @param param0.wretch - Wretch instance to use for the query
 * @returns Query options for useAdminSettingsQuery hook
 */
export default function createUseAdminSettingsQueryOptions({
  wretch: wretchInstance = createBaseWretch(),
  ...options
}: UserProvidedUseQueryOptions<AdminSettings>) {
  return createQueryOptions({
    queryKey: ['admin_settings'],
    queryFn: async () => {
      return attachCommonWretchErrorHandlers(
        wretchInstance.url('api/v1/admin-settings').get(),
        createCommonWretchErrorHandlerMap([
          ['badRequest', CoreAdminSettingsRequestError],
          ['timeout', CoreAdminSettingsServerConnectionError],
          ['internalError', CoreAdminSettingsInternalServerError],
          [502, CoreAdminSettingsServerConnectionError],
          [503, CoreAdminSettingsServerConnectionError],
          [504, CoreAdminSettingsServerConnectionError],
          ['fetchError', CoreAdminSettingsFetchError],
        ])
      )
        .json<GetAdminSettingsResponse>()
        .then((adminSettings) => {
          const maybeAdminSettings = AdminSettingsSchema(
            adminSettings.adminSettings[0]
          );
          if (maybeAdminSettings instanceof type.errors)
            throw new CoreAdminSettingsDataProcessingError(
              new Error(maybeAdminSettings.summary)
            );
          return maybeAdminSettings;
        })
        .catch((error) => {
          handleWretchFetchError(
            error,
            'admin-settings.get',
            CoreAdminSettingsFetchError
          );
          logger.error('admin-settings.get', error);
          if (error instanceof BaseError) throw error;
          throw new CoreAdminSettingsGeneralError(makeErrorFromUnknown(error));
        });
    },
    retry(failureCount, error) {
      if (error instanceof CoreAdminSettingsDataProcessingError) return false;
      return failureCount < config('misc.defaultQueryRetryCount', 3);
    },
    ...omit(options, 'variables'),
  });
}
